/* eslint-disable react/prop-types -- this codebase does not use propTypes (see CLAUDE.md). */
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { track } from "../../utils/analytics";
import { chromeIntentUrl, getBrowserContext } from "../../utils/browser";
import { returnedFromCheckout } from "./checkout";
import { safeStorage } from "../../utils/storage";
import { useStartData } from "./StartContext";
import * as startApi from "./api";
import {
  ErrorBox,
  Field,
  InfoBox,
  LinkButton,
  PrimaryButton,
  SecondaryButton,
  Section,
  Spinner,
  StickyFooter,
  TextInput,
  cx,
} from "./ui";

// Try it: the owner puts their own card in Wallet and gives themselves a test
// stamp, so they see the notification their customers will get. There is no
// API that creates a customer, so the owner signs up through their own form,
// like a customer would. Whether that already happened is read from the server
// (search by the owner's email) on every visit: in-app browsers reload pages
// and may lose storage, and a second submit with the same email makes the
// backend email the card again instead of returning wallet links.

// Set when this browser created the owner's card, so "Email me my card" only
// sends the first email. A card found any other way was either made elsewhere
// or came back through the duplicate path, which has already emailed it.
const CREATED_KEY = "start:ownerCardCreated";
// Set once the card has been emailed from this browser, so the button does
// not invite a second email. Nothing else holds the email back: the card may
// have been made on another phone, or the in-app browser lost its storage.
const EMAILED_KEY = "start:ownerCardEmailed";
// The owner said the card is in their Wallet (nothing on the server knows).
const ADDED_KEY = "start:ownerCardAdded";

// What one test adds, per card type. The other types (prepaid balance, cashback
// money, discount, coupon) have nothing that changes on a visit, so the screen
// stops at "your card is ready" for them.
const TEST_STEP = {
  stamp: { delta: 1 },
  cashback: { delta: 10 },
  membership: { delta: 1 },
};

const APP_NAMES = {
  instagram: "Instagram",
  facebook: "Facebook",
  messenger: "Messenger",
  tiktok: "TikTok",
  snapchat: "Snapchat",
  linkedin: "LinkedIn",
  line: "LINE",
};

// Black, like the wallet badges, so the add-to-wallet action reads as the
// phone's own and not as another step of ours.
const darkActionClass = cx(
  "inline-flex min-h-[52px] w-full items-center justify-center gap-2.5 rounded-xl bg-black px-5 text-base font-semibold text-white shadow-sm transition",
  "hover:bg-gray-800 active:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-60",
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-900",
);

// Same format as the backend's Customer#set_uuid (SecureRandom.hex(4)). The
// uuid becomes a URL segment, part of the Google object id and the barcode, so
// a hyphenated randomUUID() is not safe there.
function newCustomerUuid() {
  const bytes = new Uint8Array(4);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

// Stored as an object: safeStorage JSON-parses what it reads back, which turns
// an all-digit uuid into a number.
function hasMarker(key, uuid) {
  if (!uuid) return false;
  const value = safeStorage.get(key);
  return value === uuid || value?.uuid === uuid;
}

function setMarker(key, uuid) {
  safeStorage.set(key, { uuid });
}

// Any present value opts in; sending the form's own option text keeps the
// record identical to a customer who ticked the box.
function marketingConsent(form) {
  const field = form?.content?.fields?.find((f) => f?.field === "marketing");
  if (!field) return null;
  return field.options?.[0] || field.label || "yes";
}

// An email/password account may have no display name; the card would then show
// an empty member name.
function ownerName(user, store) {
  const email = user?.email || "";
  return user?.displayName?.trim() || store?.name?.trim() || email.split("@")[0];
}

async function copyText(text) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // In-app browsers often block the async clipboard; try the old way.
  }
  let area = null;
  try {
    area = document.createElement("textarea");
    area.value = text;
    area.setAttribute("readonly", "");
    // 16px so iOS does not zoom, off screen so nothing flashes.
    area.style.cssText = "position:fixed;top:0;left:-9999px;font-size:16px;opacity:0";
    document.body.appendChild(area);
    area.select();
    area.setSelectionRange(0, text.length);
    return document.execCommand("copy") === true;
  } catch {
    return false;
  } finally {
    area?.remove();
  }
}

function WalletIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="6" width="18" height="14" rx="2.5" />
      <path strokeLinecap="round" d="M3 10h18M16 15h2" />
    </svg>
  );
}

function WalletLink({ href, target, label, note }) {
  return (
    <div>
      <a href={href} className={darkActionClass} onClick={() => track("start_wallet_link_tapped", { target })}>
        <WalletIcon />
        {label}
      </a>
      {note && <p className="mt-2 text-center text-sm text-gray-500">{note}</p>}
    </div>
  );
}

function CopyLink({ url, tip }) {
  const { t } = useLanguage();
  const inputId = useId();
  const [state, setState] = useState("idle");

  const handleCopy = async () => {
    setState((await copyText(url)) ? "copied" : "manual");
  };

  return (
    <div>
      <SecondaryButton onClick={handleCopy}>
        {state === "copied" ? t("start.try.copy.copied") : t("start.try.copy.button")}
      </SecondaryButton>
      <p className="mt-2 text-sm text-gray-500" aria-live="polite">
        {tip}
      </p>
      {state === "manual" && (
        <div className="mt-3">
          <Field label={t("start.try.copy.manualLabel")} hint={t("start.try.copy.manualHint")} htmlFor={inputId}>
            <TextInput
              id={inputId}
              readOnly
              value={url}
              autoFocus
              onFocus={(event) => event.target.select()}
              className="font-mono text-sm"
            />
          </Field>
        </div>
      )}
    </div>
  );
}

// "Email me my card", or the inbox note once an email is on its way.
function EmailCard({ platform, email, canEmail, serverEmailed, emailState, onEmail, primary }) {
  const { t } = useLanguage();
  const walletName = platform === "android" ? "google" : "apple";

  if (serverEmailed || emailState === "sent") {
    return (
      <InfoBox tone="green">
        <p className="font-semibold">{t("start.try.email.sentTitle")}</p>
        <p className="mt-1">{t(`start.try.email.sentBody.${walletName}`, { email })}</p>
      </InfoBox>
    );
  }
  if (!canEmail) return null;

  const sending = emailState === "sending";
  const Button = primary ? "button" : SecondaryButton;
  return (
    <div>
      <Button
        type="button"
        onClick={onEmail}
        disabled={sending}
        aria-busy={sending || undefined}
        className={primary ? darkActionClass : undefined}
      >
        {sending && <Spinner className={cx("h-5 w-5", primary ? "text-white" : "text-gray-700")} />}
        {sending ? t("start.try.email.sending") : t("start.try.email.button")}
      </Button>
      <p className="mt-2 text-sm text-gray-500">{t("start.try.email.hint", { email })}</p>
      {emailState === "error" && (
        <div className="mt-3">
          <ErrorBox onRetry={onEmail} retryLabel={t("start.try.retry")}>
            {t("start.try.email.error")}
          </ErrorBox>
        </div>
      )}
    </div>
  );
}

function QrCodes({ urls }) {
  const { t } = useLanguage();
  const codes = [
    { id: "iphone", url: urls.apple },
    { id: "android", url: urls.google },
  ];
  return (
    <div>
      <p className="text-sm text-gray-600">{t("start.try.desktop.instructions")}</p>
      <div className="mt-4 grid grid-cols-2 gap-4">
        {codes.map((code) => (
          <figure key={code.id} className="flex flex-col items-center rounded-xl border border-gray-200 p-3">
            <QRCodeSVG
              value={code.url}
              size={136}
              level="M"
              marginSize={1}
              title={t(`start.try.desktop.${code.id}Alt`)}
              className="h-auto w-full max-w-[136px]"
            />
            <figcaption className="mt-2 text-sm font-semibold text-gray-900">
              {t(`start.try.desktop.${code.id}`)}
            </figcaption>
          </figure>
        ))}
      </div>
    </div>
  );
}

export default function TryIt() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { store, storeId, startPass, initForm } = useStartData();

  const email = user?.email || "";
  const passId = startPass?.id;
  const formUuid = initForm?.uuid;
  const cardType = startPass?.pass?.type || "stamp";
  const test = TEST_STEP[cardType] || null;
  const canTry = !!(storeId && passId && formUuid && email);
  const walletFallback = searchParams.get("wallet") === "fallback";
  const { platform, inAppBrowser } = useMemo(() => getBrowserContext(), []);

  const [lookup, setLookup] = useState(canTry ? "loading" : "ready");
  const [owner, setOwner] = useState(null);
  const [submitUrls, setSubmitUrls] = useState(null);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(false);
  const [serverEmailed, setServerEmailed] = useState(false);
  const [emailState, setEmailState] = useState("idle");
  const [addedUuid, setAddedUuid] = useState(null);
  const [stamping, setStamping] = useState(false);
  const [stampError, setStampError] = useState(false);
  const [testCount, setTestCount] = useState(0);
  const [balance, setBalance] = useState(null);

  const mounted = useRef(false);
  const busy = useRef({ create: false, stamp: false, email: false });
  const viewTracked = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (viewTracked.current) return;
    viewTracked.current = true;
    track("start_try_viewed", { card_type: cardType });
  }, [cardType]);

  // The owner's customer on this card, or null. Search returns the numeric id
  // and the current point, which the stamp and email calls need.
  const findOwner = useCallback(async () => {
    const customers = await startApi.findCustomersByEmail(storeId, email);
    return customers.find((c) => c?.pass && String(c.pass.id) === String(passId)) || null;
  }, [storeId, email, passId]);

  const lookupOwner = useCallback(async () => {
    setLookup("loading");
    try {
      const found = await findOwner();
      if (!mounted.current) return;
      setOwner(found);
      setLookup("ready");
    } catch {
      if (mounted.current) setLookup("error");
    }
  }, [findOwner]);

  useEffect(() => {
    if (canTry) lookupOwner();
  }, [canTry, lookupOwner]);

  const createCard = async () => {
    if (busy.current.create || !canTry) return;
    busy.current.create = true;
    setCreating(true);
    setCreateError(false);
    const uuid = newCustomerUuid();
    const consent = marketingConsent(initForm);
    const submission = {
      name: ownerName(user, store),
      email,
      ...(consent ? { marketing: [consent] } : {}),
      uuid,
    };
    try {
      let response = null;
      try {
        response = await startApi.submitPublicForm(formUuid, submission);
      } catch (error) {
        // A 5xx or a dropped connection can still have saved the customer, so
        // look before reporting a failure (a retry would take the duplicate
        // path and email the card).
        const status = error?.response?.status;
        if (status && status < 500) throw error;
      }
      const links = response?.pkpass_url;
      const fromSubmit =
        links && !Array.isArray(links) && links.apple_wallet_url
          ? { apple: links.apple_wallet_url, google: links.google_wallet_url }
          : null;

      let found = null;
      try {
        found = await findOwner();
      } catch {
        // The card exists if the submit returned links; the id is looked up
        // again before the first stamp or email.
      }
      if (!found && fromSubmit) found = { id: null, uuid, point: null };
      if (!found) throw new Error("Owner card not found after submit");

      const createdHere = found.uuid === uuid;
      if (createdHere) {
        setMarker(CREATED_KEY, uuid);
        track("start_owner_card_created", { card_type: cardType });
      } else if (response) {
        // The duplicate path: the server emailed the card instead.
        setServerEmailed(true);
      }
      if (!mounted.current) return;
      setSubmitUrls(createdHere ? fromSubmit : null);
      setOwner(found);
    } catch {
      if (mounted.current) setCreateError(true);
    } finally {
      busy.current.create = false;
      if (mounted.current) setCreating(false);
    }
  };

  const emailCard = async () => {
    if (busy.current.email || !owner) return;
    busy.current.email = true;
    setEmailState("sending");
    try {
      let customerId = owner.id;
      if (!customerId) {
        const fresh = await findOwner();
        customerId = fresh?.id;
        if (fresh && mounted.current) setOwner(fresh);
      }
      if (!customerId) throw new Error("Owner card not found");
      await startApi.sendPassEmail(storeId, customerId);
      setMarker(EMAILED_KEY, owner.uuid);
      track("start_card_emailed", { platform });
      if (mounted.current) setEmailState("sent");
    } catch {
      if (mounted.current) setEmailState("error");
    } finally {
      busy.current.email = false;
    }
  };

  const markAdded = () => {
    if (!owner) return;
    setMarker(ADDED_KEY, owner.uuid);
    setAddedUuid(owner.uuid);
    track("start_wallet_added", { card_type: cardType, platform });
  };

  // `point` is an absolute balance, read fresh right before: the card starts
  // at the type's initial points, and a double tap then lands on the same value.
  const giveTestStamp = async () => {
    if (busy.current.stamp || !test) return;
    busy.current.stamp = true;
    setStamping(true);
    setStampError(false);
    try {
      const fresh = await findOwner();
      if (!fresh?.id) throw new Error("Owner card not found");
      const target = (Number(fresh.point) || 0) + test.delta;
      const updated = await startApi.setCustomerPoint(storeId, fresh.id, target);
      const point = Number(updated?.point);
      const now = Number.isFinite(point) ? point : target;
      track("start_test_stamp", { type: cardType });
      if (!mounted.current) return;
      setOwner({ ...fresh, point: now });
      setBalance(now);
      setTestCount((count) => count + 1);
    } catch {
      if (mounted.current) setStampError(true);
    } finally {
      busy.current.stamp = false;
      if (mounted.current) setStamping(false);
    }
  };

  // Someone who already went through checkout is past the plan step.
  const nextStep = () => (returnedFromCheckout("") ? "/start/done" : "/start/plan");
  const goNext = () => navigate(nextStep());
  const skip = () => {
    track("start_try_skipped", { has_card: !!owner });
    navigate(nextStep());
  };

  const urls = useMemo(() => {
    if (!owner?.uuid) return null;
    return submitUrls || startApi.walletUrls(owner.uuid);
  }, [owner?.uuid, submitUrls]);

  const intentUrl = useMemo(() => {
    if (!urls?.google) return null;
    try {
      return chromeIntentUrl(urls.google, `${window.location.origin}/start/try?wallet=fallback`);
    } catch {
      return null;
    }
  }, [urls?.google]);

  const added = useMemo(
    () => !!owner?.uuid && (addedUuid === owner.uuid || hasMarker(ADDED_KEY, owner.uuid)),
    [owner?.uuid, addedUuid],
  );
  const canEmail = !!owner?.uuid && !serverEmailed && !hasMarker(EMAILED_KEY, owner.uuid);
  const needsCard = canTry && lookup !== "error" && !owner;
  const introKey = test ? cardType : "other";
  const appName = APP_NAMES[inAppBrowser];

  const emailProps = {
    platform,
    email,
    canEmail,
    serverEmailed,
    emailState,
    onEmail: emailCard,
  };

  const renderWalletActions = () => {
    if (!urls) return null;
    if (platform === "ios" && inAppBrowser) {
      return (
        <div className="space-y-4">
          <InfoBox tone="amber">
            {appName ? t("start.try.ios.inApp", { app: appName }) : t("start.try.ios.inAppGeneric")}
          </InfoBox>
          <EmailCard {...emailProps} primary />
          <CopyLink url={urls.apple} tip={t("start.try.ios.copyTip")} />
        </div>
      );
    }
    if (platform === "ios") {
      return (
        <WalletLink
          href={urls.apple}
          target="apple"
          label={t("start.try.apple.button")}
          note={t("start.try.apple.note")}
        />
      );
    }
    if (platform === "android" && inAppBrowser) {
      if (walletFallback) {
        // Chrome did not open (Android sent us to the fallback URL).
        return (
          <div className="space-y-4">
            <InfoBox tone="amber">{t("start.try.android.fallback")}</InfoBox>
            <EmailCard {...emailProps} primary />
            {!canEmail && !serverEmailed && emailState !== "sent" && (
              <>
                <CopyLink url={urls.google} tip={t("start.try.android.copyTip")} />
                <div className="text-center">
                  <a
                    href={urls.google}
                    onClick={() => track("start_wallet_link_tapped", { target: "google" })}
                    className="inline-flex min-h-11 items-center px-2 text-sm font-semibold text-[#4522ED] underline-offset-4 hover:underline"
                  >
                    {t("start.try.android.tryHere")}
                  </a>
                </div>
              </>
            )}
          </div>
        );
      }
      return (
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            {appName ? t("start.try.android.inApp", { app: appName }) : t("start.try.android.inAppGeneric")}
          </p>
          {intentUrl && (
            <WalletLink
              href={intentUrl}
              target="intent"
              label={t("start.try.android.openChrome")}
              note={t("start.try.android.openChromeNote")}
            />
          )}
          <EmailCard {...emailProps} />
        </div>
      );
    }
    if (platform === "android") {
      return (
        <WalletLink
          href={urls.google}
          target="google"
          label={t("start.try.google.button")}
          note={t("start.try.google.note")}
        />
      );
    }
    return <QrCodes urls={urls} />;
  };

  const balanceText =
    balance === null ? null : t(`start.try.test.${cardType}.balance`, { count: balance });

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">{t("start.try.title")}</h1>
        <p className="mt-1 text-base text-gray-600">{t(`start.try.intro.${introKey}`)}</p>
      </div>

      {startPass?.preview_image_url && (
        <img
          src={startPass.preview_image_url}
          alt={t("start.try.previewAlt")}
          className="mx-auto w-full max-w-[280px] rounded-2xl shadow-md"
        />
      )}

      {lookup === "loading" && (
        <div className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-white p-4" role="status">
          <Spinner className="h-5 w-5 text-[#4522ED]" />
          <span className="text-sm text-gray-600">{t("start.try.loading")}</span>
        </div>
      )}

      {lookup === "error" && (
        <ErrorBox onRetry={lookupOwner} retryLabel={t("start.try.retry")}>
          {t("start.try.lookupError")}
        </ErrorBox>
      )}

      {lookup === "ready" && needsCard && (
        <section className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-5">
          <h2 className="text-lg font-semibold text-gray-900">{t("start.try.create.title")}</h2>
          <p className="mt-1 text-sm text-gray-600">{t("start.try.create.description", { email })}</p>
          {creating && (
            <p role="status" className="mt-3 text-sm font-medium text-gray-700">
              {t("start.try.create.working")}
            </p>
          )}
          {createError && (
            <div className="mt-3">
              <ErrorBox onRetry={createCard} retryLabel={t("start.try.retry")}>
                {t("start.try.create.error")}
              </ErrorBox>
            </div>
          )}
        </section>
      )}

      {owner && (
        <Section title={t("start.try.add.title")}>
          {renderWalletActions()}
          {!added && (
            <div className="border-t border-gray-100 pt-4">
              <SecondaryButton onClick={markAdded}>{t("start.try.added.button")}</SecondaryButton>
              <p className="mt-2 text-center text-sm text-gray-500">{t("start.try.added.hint")}</p>
            </div>
          )}
        </Section>
      )}

      {owner && added && test && (
        <Section title={t("start.try.test.title")} description={t("start.try.test.description")}>
          <SecondaryButton onClick={giveTestStamp} disabled={stamping} aria-busy={stamping || undefined}>
            {stamping && <Spinner className="h-5 w-5 text-gray-700" />}
            {t(`start.try.test.${cardType}.${testCount > 0 ? "again" : "button"}`, { points: test.delta })}
          </SecondaryButton>
          <div aria-live="polite">
            {testCount > 0 && !stamping && (
              <InfoBox tone="green">
                <p className="font-semibold">{t("start.try.test.watch")}</p>
                {balanceText && <p className="mt-1">{balanceText}</p>}
              </InfoBox>
            )}
          </div>
          {stampError && (
            <ErrorBox onRetry={giveTestStamp} retryLabel={t("start.try.retry")}>
              {t("start.try.test.error")}
            </ErrorBox>
          )}
          {testCount > 0 && <p className="text-sm text-gray-500">{t("start.try.test.noNotification")}</p>}
        </Section>
      )}

      {owner && added && !test && (
        <InfoBox tone="green">
          <p className="font-semibold">{t("start.try.ready.title")}</p>
          <p className="mt-1">{t("start.try.ready.body")}</p>
        </InfoBox>
      )}

      <InfoBox tone="gray">{t("start.try.customersNote")}</InfoBox>

      <StickyFooter>
        {needsCard ? (
          <PrimaryButton onClick={createCard} loading={creating || lookup === "loading"}>
            {creating ? t("start.try.create.buttonWorking") : t("start.try.create.button")}
          </PrimaryButton>
        ) : (
          <PrimaryButton onClick={goNext}>{t("start.try.next")}</PrimaryButton>
        )}
        {!added && (
          <div className="mt-1 flex justify-center">
            <LinkButton onClick={skip}>{t("start.try.skip")}</LinkButton>
          </div>
        )}
      </StickyFooter>
    </div>
  );
}
